import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import { currentKey, getCurrent, JourneyError, type Drift } from "./api";
import { driftLines, NO_RELEASE_TEXT, releaseLine, shortHash } from "./format";

/** The selection lives in the URL (UX §2), so "Copy link" hands over exactly this view. */
export function useJourneySearch() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  return {
    values,
    set: (next: FilterValues) => void navigate({ to: ".", search: applyFilters(search, next), replace: true }),
  };
}

export const currentQuery = () => ({ queryKey: currentKey, queryFn: getCurrent, retry: false });

export function isNoRelease(error: unknown): boolean {
  return error instanceof JourneyError && error.key === "no_release";
}

/**
 * The header both tabs share (H8): release, commit, walked bundle and canvas version, a drift badge
 * when the published canvas has moved, and Copy link. Not a freshness line: the shell draws that.
 */
export function JourneyHeader({ drift = [] }: { drift?: Drift[] }) {
  const current = useQuery(currentQuery());
  const c = current.data?.data;
  const lines = driftLines(drift);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm text-ks-ink">
        {c ? (
          <>
            <span className="font-medium">{releaseLine(c.release, c.canvas?.version)}</span>
            <span className="text-ks-grey-1">
              commit {shortHash(c.commit)} · bundle {shortHash(c.bundle_md5)}
            </span>
          </>
        ) : current.isError && !isNoRelease(current.error) ? (
          <span className="text-ks-grey-1">
            Could not load the release.{" "}
            <Button variant="ghost" size="xs" onClick={() => void current.refetch()}>
              Try again
            </Button>
          </span>
        ) : (
          <span className="text-ks-grey-1">{current.isLoading ? "Loading release…" : "no capture yet"}</span>
        )}
        {lines.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" tabIndex={0} className="border-ks-warn text-ks-warn">
                  drift
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <ul>
                  {lines.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <CopyLink />
    </div>
  );
}

function CopyLink() {
  const [state, setState] = useState<"idle" | "copied" | "manual">("idle");
  const href = typeof window === "undefined" ? "" : window.location.href;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setState("copied");
    } catch {
      setState("manual");
    }
  };
  return (
    <div className="flex items-center gap-2">
      {state === "manual" && (
        <label className="flex items-center gap-2 text-xs text-ks-grey-1">
          Link
          <Input readOnly data-nonmutating value={href} onFocus={(e) => e.currentTarget.select()} className="w-72" />
        </label>
      )}
      <Button variant="secondary" size="sm" onClick={copy}>
        Copy link
      </Button>
      <span aria-live="polite" className="text-xs text-ks-grey-1">
        {state === "copied" ? "Link copied" : ""}
      </span>
    </div>
  );
}

export function NoRelease() {
  return (
    <Alert role="status">
      <AlertTitle>No production capture yet</AlertTitle>
      <AlertDescription>{NO_RELEASE_TEXT}</AlertDescription>
    </Alert>
  );
}

export function LoadError({ title, error, retry }: { title: string; error: unknown; retry: () => void }) {
  const detail = error instanceof JourneyError ? error.message : null;
  return (
    <Alert variant="destructive">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {detail && <p>{detail}</p>}
        <Button variant="secondary" size="sm" className="mt-2" onClick={retry}>
          Try again
        </Button>
      </AlertDescription>
    </Alert>
  );
}
